import { useCallback, useEffect, useState } from "react";
import { api } from "@/services/api";
import { cmsApi, type CmsPage } from "@/services/pages";
import GeneralTab from "./components/GeneralTab";
import SeoTab from "./components/SeoTab";
import MaintenanceTab from "./components/MaintenanceTab";
import AnalyticsTab from "./components/AnalyticsTab";
import EmailTab from "./components/EmailTab";
import RolesTab from "./components/RolesTab";
import AdvancedTab from "./components/AdvancedTab";

interface Setting {
  id: number;
  key: string;
  value: string;
  group_name: string;
  is_public: number;
}

type TabKey = "general" | "maintenance" | "seo" | "analytics" | "email" | "roles" | "advanced";

const TABS: { key: TabKey; label: string; icon: string }[] = [
  { key: "general", label: "General", icon: "ri-settings-2-line" },
  { key: "maintenance", label: "Maintenance", icon: "ri-shut-down-line" },
  { key: "seo", label: "SEO & Meta Tags", icon: "ri-seo-line" },
  { key: "analytics", label: "Analytics", icon: "ri-bar-chart-box-line" },
  { key: "email", label: "Email Notifications", icon: "ri-mail-settings-line" },
  { key: "roles", label: "Custom Roles", icon: "ri-shield-user-line" },
  { key: "advanced", label: "Advanced", icon: "ri-settings-3-line" },
];

export default function AdminSettings() {
  const [activeTab, setActiveTab] = useState<TabKey>("seo");
  const [values, setValues] = useState<Record<string, string>>();
  const [pages, setPages] = useState<CmsPage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [settingsRes, pagesRes] = await Promise.all([
        api.get<Setting[]>("/settings"),
        cmsApi.list(),
      ]);
      const map: Record<string, string> = {};
      settingsRes.forEach((s) => {
        map[s.key] = s.value;
      });
      setValues(map);
      setPages(pagesRes);
    } catch (e: any) {
      setError(e?.message || "Failed to load settings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function refreshPages() {
    try {
      const pagesRes = await cmsApi.list();
      setPages(pagesRes);
    } catch {
      // best-effort refresh
    }
  }

  function update(key: string, value: string) {
    setValues((prev) => ({ ...prev, [key]: value }));
  }

  async function save() {
    setSaving(true);
    setError("");
    setSaved(false);
    try {
      // Build payload from all known setting keys used across tabs
      const knownKeys = new Set<string>([
        // General
        "site_name", "site_tagline", "site_url", "brand_logo_url", "brand_favicon_url",
        "seo_title", "seo_description", "seo_keywords", "og_image_url",
        "contact_phone", "contact_whatsapp", "contact_email", "contact_address",
        "social_facebook", "social_instagram", "social_linkedin",
        "brochure_url",
        // Maintenance
        "maintenance_mode", "maintenance_message",
        // Email
        "notification_email", "email_sender_name",
        "notify_new_lead", "notify_new_deal", "notify_property_approved", "notify_daily_summary",
        // Advanced
        "ga_id", "gtm_id", "fb_pixel_id", "custom_header_script",
        "allow_registration", "enforce_https", "enable_captcha", "cache_enabled",
      ]);

      const payload: { key: string; value: string; group_name: string; is_public: number }[] = [];

      knownKeys.forEach((key) => {
        payload.push({
          key,
          value: values[key] ?? "",
          group_name: deriveGroup(key),
          is_public: 1,
        });
      });

      // Preserve any extra keys not in the known list
      Object.keys(values).forEach((key) => {
        if (!knownKeys.has(key)) {
          payload.push({
            key,
            value: values[key] ?? "",
            group_name: "general",
            is_public: 1,
          });
        }
      });

      await api.put("/settings", payload);
      setSaved(true);
      window.dispatchEvent(new Event("site-settings-updated"));
    } catch (e: any) {
      setError(e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  const today = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-xl text-foreground-950">Site Settings</h1>
          <p className="text-xs text-foreground-600">{today}</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="px-3 py-1.5 rounded-full bg-primary-100 text-primary-800 text-xs font-semibold">
            <i className="ri-dashboard-line mr-1"></i> Admin Panel
          </span>
          <span className="flex items-center justify-center w-8 h-8 rounded-full bg-primary-600 text-background-50 text-sm font-bold">
            A
          </span>
        </div>
      </div>

      {/* Sub-header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-foreground-950">Site Settings</h2>
          <p className="text-xs text-foreground-600">
            Manage global settings, maintenance mode, and SEO defaults
          </p>
        </div>
        <button
          onClick={save}
          disabled={saving || loading}
          className="inline-flex items-center gap-2 text-sm font-semibold whitespace-nowrap rounded-full bg-primary-600 text-background-50 px-5 py-2.5 hover:bg-primary-700 disabled:opacity-60"
        >
          {saving ? (
            <>
              <i className="ri-loader-4-line animate-spin"></i> Saving...
            </>
          ) : (
            <>
              <i className="ri-save-line"></i> Save Settings
            </>
          )}
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <div className="rounded-2xl bg-accent-100 border border-accent-300 p-4 text-sm text-accent-900">
          {error}
        </div>
      )}
      {saved && (
        <div className="rounded-2xl bg-primary-50 border border-primary-200 p-4 text-sm text-primary-900">
          Settings saved successfully.
        </div>
      )}

      {/* Tabs */}
      <div className="rounded-full bg-background-100 border border-background-200 p-1 flex items-center gap-1 overflow-x-auto">
        {TABS.map((t) => {
          const isActive = activeTab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                isActive
                  ? "bg-background-50 text-foreground-950 shadow-sm border border-background-200"
                  : "text-foreground-600 hover:text-foreground-800 hover:bg-background-200/60"
              }`}
            >
              <i className={t.icon}></i> {t.label}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      {loading ? (
        <p className="py-16 text-center text-foreground-600">
          <i className="ri-loader-4-line animate-spin"></i> Loading...
        </p>
      ) : (
        <div>
          {activeTab === "general" && <GeneralTab values={values} onUpdate={update} />}
          {activeTab === "maintenance" && <MaintenanceTab values={values} onUpdate={update} />}
          {activeTab === "seo" && <SeoTab pages={pages} siteUrl={values.site_url || ""} onPagesChange={setPages} />}
          {activeTab === "analytics" && <AnalyticsTab />}
          {activeTab === "email" && <EmailTab values={values} onUpdate={update} />}
          {activeTab === "roles" && <RolesTab />}
          {activeTab === "advanced" && <AdvancedTab values={values} onUpdate={update} />}
        </div>
      )}
    </div>
  );
}

function deriveGroup(key: string): string {
  if (key.startsWith("brand_") || key.startsWith("site_") || key.startsWith("seo_") || key.startsWith("og_") || key === "brochure_url") return "general";
  if (key.startsWith("contact_")) return "contact";
  if (key.startsWith("social_")) return "social";
  if (key.startsWith("maintenance_")) return "maintenance";
  if (key.startsWith("notify_") || key.startsWith("email_") || key === "notification_email") return "email";
  return "advanced";
}